import { RentalType } from "@/shared/types/enums";
import { dateLocalToIso } from "@/shared/lib/date-format";
import { formatDate } from "@/shared/lib/formatters";
import type { RentalCreateFormSchema } from "@/features/rentals/components/rental-create/rental-create-schema";
import type { CreateRentalDTO, PaymentDTO } from "../types";

/**
 * Formata uma data `yyyy-MM-dd` do wizard admin para exibição.
 *
 * Passa pelos formatters defensivos (`formatDate` devolve "—" para valor
 * inválido) — ver CLAUDE.md § "Datas e robustez de runtime".
 */
export function formatAdminPeriodLabel(value: string): string | null {
  if (!value) return null;
  try {
    // `dateLocalToIso` LANÇA RangeError em valor inválido — blindá-la é
    // pendência conhecida (docs/PENDING.md). Este rótulo é só de exibição:
    // alimenta o painel de resumo e a revisão a cada tecla, então não pode
    // derrubar a tela por um valor intermediário.
    const label = formatDate(dateLocalToIso(value));
    return label === "—" ? null : label;
  } catch {
    return null;
  }
}

/**
 * Monta o `CreateRentalDTO` de `/api/admin/rentals` a partir do formulário
 * do wizard de criação admin.
 *
 * Diferenças em relação ao builder do fluxo USER
 * (`rental-create-payload.ts`), todas deliberadas:
 * - envia `contractId` (o contrato já existe: o `ContractSelector` cria
 *   cliente e contrato antes de chegar aqui), nunca cliente/contrato
 *   embutidos, que só o endpoint USER aceita;
 * - datas vêm sem hora, então converte com `dateLocalToIso`
 *   (meia-noite local) e não com `datetimeLocalToIso`;
 * - suporta aluguel retroativo (`outAt`/`inAt`), que o USER não tem.
 *
 * @param contractId contrato selecionado — validado antes de chamar.
 */
export function buildAdminCreatePayload(
  data: RentalCreateFormSchema,
  contractId: number,
  sellerId: number,
): CreateRentalDTO {
  const isSuit = data.rentalType === RentalType.SUIT;

  return {
    contractId,
    sellerId,
    rentalType: data.rentalType,
    rentedAt: new Date().toISOString(),
    hasShoes: isSuit ? data.hasShoes : false,
    ...(isSuit && data.hasShoes
      ? { shoesPairQuantity: data.shoesPairQuantity }
      : {}),
    stockIds: isSuit ? data.stockIds : undefined,
    gownItems: !isSuit ? data.gownItems : undefined,
    description: data.description,
    tagIds: data.tagIds,
    totalValue: data.totalValue,
    // Interpreta como meia-noite local (não UTC) — preserva a data civil
    // escolhida pelo admin. Ver dateLocalToIso.
    expectedOutAt: dateLocalToIso(data.expectedOutAt),
    expectedInAt: dateLocalToIso(data.expectedInAt),
    // Aluguel retroativo: datas efetivas em que a peça saiu e voltou.
    ...(data.isRetroactive && data.outAt && data.inAt
      ? {
          outAt: dateLocalToIso(data.outAt),
          inAt: dateLocalToIso(data.inAt),
        }
      : {}),
    payments: data.payments as PaymentDTO[],
    emitInvoice: data.emitInvoice,
    ...(data.invoiceObservation?.trim()
      ? { invoiceObservation: data.invoiceObservation.trim() }
      : {}),
  };
}
